import { useCustomsDocsApi } from "@/Hooks/useCustomsDocsApi";
import { CustomsDocsUploadMode } from "@/Apis/CustomsDocsUploadMode";
import type { UUID } from "@/Shared/types";
import { CustomDocsSubmitSection } from "@/Shared/CustomDocs/CustomComponents/CustomDocsSubmitSection";
import { useCustomDocsDropzone } from "@/Shared/CustomDocs/Hooks/useCustomDocsDropzone";
import { useState } from "react";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";

interface UploadCustomsDocsDialogBodyProps {
  trackingId: UUID;
  onFinished: () => void;
  onCancel: () => void;
}

export function UploadCustomsDocsDialogBody(
  props: UploadCustomsDocsDialogBodyProps,
) {
  const customsDocsApi = useCustomsDocsApi();
  const [loading, setLoading] = useState(false);
  const [loadingSuccess, setLoadingSuccess] = useState(false);
  const [loadingFailure, setLoadingFailure] = useState(false);

  const { files, setFiles, dropzoneState } = useCustomDocsDropzone();

  async function onUpload() {
    setLoadingSuccess(false);
    setLoadingFailure(false);
    setLoading(true);
    try {
      await customsDocsApi.uploadWithTrackingId(
        props.trackingId,
        CustomsDocsUploadMode.Initial,
        files,
      );
      setLoadingSuccess(true);
      setTimeout(function () {
        props.onFinished();
      }, 1000);
    } catch (e) {
      console.error(`Failure uploading customs docs`, { e });
      setLoadingFailure(true);
    } finally {
      setLoading(false);
    }
  }

  function acceptedFilesCount() {
    if (files.length === 0) {
      return "";
    }

    if (files.length === 1) {
      return " (1 file)";
    }

    if (files.length > 1) {
      return ` (${files.length} file)`;
    }

    // should not be here
    throw new Error("Invalid files count");
  }

  return (
    // CustomDocsSubmitSection carries its own spacing below the dropzone, so
    // the stack adds none of its own.
    <Stack gap={0} align="center">
      <CustomDocsSubmitSection
        setFiles={setFiles}
        dropzoneState={dropzoneState}
        files={files}
        subtitle={
          <>
            <p>
              If you have the customs documents, please upload them here. This
              will help ensure this shipment goes smoothly.
            </p>
            <p>A copy must also be provided to the driver at pickup.</p>
          </>
        }
      />
      <Button
        primary
        size="large"
        onClick={onUpload}
        loadingSuccess={loadingSuccess}
        loading={loading}
        loadingFailure={loadingFailure}
        disabled={files.length === 0 || loading || loadingSuccess}
        disabledMessage="Add at least one customs document to upload"
      >
        Upload Customs Docs {acceptedFilesCount()}
      </Button>
    </Stack>
  );
}
